import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { HardHat, Loader2 } from "lucide-react";

const DEMO = [
  { label: "Salesperson", email: "sales@amaaconstruction.com" },
  { label: "Office", email: "office@amaaconstruction.com" },
  { label: "Admin", email: "pat@redphoenixfr.com" },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Bathroom remodel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center shadow-xl shadow-orange-500/40">
              <HardHat className="w-7 h-7 text-slate-950" />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl tracking-wide text-white">AMAA CONSTRUCTION</div>
              <div className="text-xs text-orange-400 uppercase tracking-[0.3em]">Field Sales & Estimating AI</div>
            </div>
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight text-white max-w-md">
            An experienced estimator, right in your pocket.
          </h1>
          <p className="text-slate-400 mt-4 max-w-md">
            Talk through the job. Capture photos and measurements. Send a clean package to the office and get pricing back in minutes.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
              <HardHat className="w-6 h-6 text-slate-950" />
            </div>
            <span className="font-display font-extrabold text-2xl">AMAA</span>
          </div>
          <h2 className="font-display text-3xl font-bold mb-1">Sign in</h2>
          <p className="text-slate-500 text-sm mb-8">Private application — authorized team members only.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-400">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@amaaconstruction.com"
                className="mt-1.5 bg-slate-900 border-slate-700 h-12 text-base"
                required
              />
            </div>
            <div>
              <Label className="text-slate-400">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 bg-slate-900 border-slate-700 h-12 text-base"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold text-base hover:scale-[1.01] transition-transform"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="mt-8 border-t border-slate-800 pt-4">
            <div className="text-xs text-slate-500 mb-2">Demo accounts (password: amaa2026)</div>
            <div className="flex flex-wrap gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-fill-${d.label.toLowerCase()}`}
                  onClick={() => { setEmail(d.email); setPassword("amaa2026"); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-900 border border-slate-700 text-slate-300 hover:border-orange-500/50 hover:text-orange-300 transition-colors"
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
